import { findAmountOfCommentsByEntityIds as findExternalCommentsCount } from "@synthesis/shared-services/comments/external/client";
import { findAmountOfCommentsByEntityIds as findInternalCommentsCount } from "@synthesis/shared-services/comments/internal/client";
import { useGet2 as useGetExternalPayment } from "@synthesis/shared-services/external/payment/hooks";
import { Counter, TabGroup } from "@synthesis/shared-ui";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";

import { useClientType } from "../../../shared/hooks/use-client-type";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import {
  getVisibleApplicationDetailTabs,
  isApplicationDetailTabDisabled,
  normalizeApplicationDetailActiveTab,
  resolveApplicationDetailTabVisibilityContext,
} from "../application-detail-tabs.lib";
import { APPLICATION_COMMENTS_COUNT_QUERY_KEY, usePageStore, type ApplicationDetailTabKey } from "../application-detail-page.lib";

export const ApplicationDetailTabGroup = observer(() => {
  const pageStore = usePageStore();
  const clientType = useClientType();
  const roles = useUserRoles();
  const application = pageStore.application;
  const requestId = application?.requestId ?? "";

  const commentsCountQuery = useQuery({
    queryKey: APPLICATION_COMMENTS_COUNT_QUERY_KEY(requestId, clientType ?? "external"),
    queryFn: async ({ signal }) => {
      const fetchCommentsCount = clientType === "internal" ? findInternalCommentsCount : findExternalCommentsCount;
      return (await fetchCommentsCount({ body: [requestId], signal })).data;
    },
    enabled: Boolean(requestId && clientType),
    select: (data) => data[requestId] ?? 0,
  });

  const { data: externalPaymentView } = useGetExternalPayment(
    { path: { id: application?.requestId ?? "" } },
    {
      query: { enabled: clientType === "external" && !!application?.requestId },
    },
  );

  const tabVisibilityContext = useMemo(() => {
    if (!application || !clientType) {
      return null;
    }

    return resolveApplicationDetailTabVisibilityContext({
      clientType,
      status: application.status,
      roles,
      applicantData: application.applicantData,
      paymentTerms: application.paymentTermsValue,
      explicitPaidService: application.isPaidServiceRequired || externalPaymentView?.invoiceReceived === true,
      hasFinalDecision: application.hasFinalDecision,
    });
  }, [application, clientType, roles, externalPaymentView]);

  const visibleTabs = useMemo(() => {
    if (!tabVisibilityContext) {
      return [];
    }

    return getVisibleApplicationDetailTabs(tabVisibilityContext);
  }, [tabVisibilityContext]);

  useEffect(() => {
    if (!application || !tabVisibilityContext) {
      return;
    }

    const nextActiveTab = normalizeApplicationDetailActiveTab(pageStore.activeTab, visibleTabs, tabVisibilityContext);

    if (nextActiveTab !== pageStore.activeTab) {
      pageStore.setActiveTab(nextActiveTab);
    }
  }, [application, pageStore, tabVisibilityContext, visibleTabs]);

  const tabGroupItems = useMemo(() => {
    if (!application || !tabVisibilityContext) {
      return [];
    }

    const documentsCount = application.documents.reduce((sum, doc) => sum + doc.files.length, 0);

    const tabCounts: Partial<Record<ApplicationDetailTabKey, number>> = {
      documents: documentsCount,
      comments: commentsCountQuery.data ?? 0,
    };

    return visibleTabs.map((tab) => {
      const count = tabCounts[tab.key];
      const disabled = isApplicationDetailTabDisabled(tab.key, tabVisibilityContext);

      return {
        key: tab.key,
        value: tab.key,
        disabled,
        title: (
          <span className="inline-flex flex-nowrap items-center">
            <span>{tab.label}</span>
            &nbsp;
            {count && count > 0 ? <Counter count={count} warningColor={false} /> : null}
          </span>
        ),
      };
    });
  }, [application, commentsCountQuery.data, tabVisibilityContext, visibleTabs]);

  if (!clientType) {
    return null;
  }

  return (
    <TabGroup
      className="w-full min-w-0"
      tabs={tabGroupItems}
      activeKey={pageStore.activeTab}
      onChange={(value) => pageStore.setActiveTab(value as ApplicationDetailTabKey)}
    />
  );
});
